import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { IPerfilComponente } from "../types/perfil.types";
import { buscarPerfisParaComposicao } from "../services/perfilService";
import { debounce } from "lodash";

// Estilos padronizados para inputs (igual à aba GERAL)
const inputSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiInputBase-input": {
    padding: "20px 14px 8px 14px",
  },
  minHeight: 56,
};

const selectSx = {
  backgroundColor: "#f0f0f0",
  borderRadius: 0,
  minHeight: 56,
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "20px 14px 8px 14px",
  },
};

const labelSx = {
  color: "#3C90AD",
  fontWeight: 500,
  fontSize: "0.8rem",
  "&.Mui-focused": {
    color: "#3C90AD",
  },
};

interface TabComposicaoProps {
  composicao: IPerfilComponente[];
  onChange: (composicao: IPerfilComponente[]) => void;
  figuraUrl?: string;
  onOpenMidias: () => void;
}

export const TabComposicao: React.FC<TabComposicaoProps> = ({
  composicao,
  onChange,
  figuraUrl,
  onOpenMidias,
}) => {
  const [itemParaRemover, setItemParaRemover] = useState<number | null>(null);
  const [opcoesPerfis, setOpcoesPerfis] = useState<Array<{ idPerfil: number; codigo: string; descricao: string; tipoPerfil: string; perfilComposto: number }>>([]);
  const [carregandoPerfis, setCarregandoPerfis] = useState(false);

  // Buscar perfis com debounce
  const buscarPerfis = useCallback(
    debounce(async (termo: string) => {
      if (!termo || termo.length < 2) {
        setOpcoesPerfis([]);
        return;
      }
      setCarregandoPerfis(true);
      try {
        const perfis = await buscarPerfisParaComposicao(termo);
        setOpcoesPerfis(perfis);
      } catch (error) {
        console.error("Erro ao buscar perfis:", error);
        setOpcoesPerfis([]);
      } finally {
        setCarregandoPerfis(false);
      }
    }, 300),
    []
  );

  // Plano 17/09/2026 (TI CEM, 15/09): o mesmo código pode entrar mais de uma vez na
  // composição — o aviso "Código duplicado" foi removido (a API e o banco já aceitavam).

  const handleAdicionar = () => {
    const novaSequencia = composicao.length > 0
      ? Math.max(...composicao.map((c) => c.sequencia || 0)) + 1
      : 1;

    onChange([
      {
        sequencia: novaSequencia,
        codigoComponente: "",
        descricaoComponente: "",
        tipoPerfilComponente: "interno",
      },
      ...composicao, // Novo item no topo
    ]);
  };

  const handleAlterarCampo = (
    sequencia: number,
    campo: keyof IPerfilComponente,
    valor: any
  ) => {
    onChange(
      composicao.map((c) =>
        c.sequencia === sequencia ? { ...c, [campo]: valor } : c
      )
    );
  };

  const handleRemover = (sequencia: number) => {
    onChange(composicao.filter((c) => c.sequencia !== sequencia));
    setItemParaRemover(null);
  };

  return (
    <Grid container spacing={3}>
      {/* Coluna Esquerda - Imagem do Produto */}
      <Grid item xs={12} md={3}>
        {/* Container da Imagem - COM BORDA CINZA */}
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            p: 2,
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: "#333", mb: 2, fontWeight: 500 }}
          >
            Imagem do produto
          </Typography>
          
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 180,
              backgroundColor: "#fff",
            }}
          >
            {figuraUrl ? (
              <img
                src={figuraUrl}
                alt="Imagem do produto"
                style={{ maxWidth: "100%", maxHeight: 180, objectFit: "contain" }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhuma imagem
              </Typography>
            )}
          </Box>
        </Box>

        {/* Botão FORA do container da imagem */}
        <Button
          variant="contained"
          fullWidth
          onClick={onOpenMidias}
          sx={{
            backgroundColor: "#3C90AD",
            "&:hover": { backgroundColor: "#2d6f87" },
            textTransform: "none",
            py: 1.2,
          }}
        >
          GERENCIAMENTO DE MÍDIAS
        </Button>
      </Grid>

      {/* Coluna Direita - Lista de Componentes */}
      <Grid item xs={12} md={9}>
        {/* Botão Adicionar */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdicionar}
            sx={{
              backgroundColor: "#3C90AD",
              "&:hover": { backgroundColor: "#2d6f87" },
            }}
          >
            ADICIONAR PRODUTO
          </Button>
        </Box>

        {/* Lista de Componentes */}
        {composicao.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: "center", backgroundColor: "#fafafa" }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum componente adicionado ainda.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {composicao.map((comp) => {
              return (
                <Paper
                  key={comp.sequencia}
                  sx={{
                    p: 2,
                    backgroundColor: "#f5f5f5",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <Autocomplete
                        freeSolo
                        options={opcoesPerfis}
                        getOptionLabel={(option) => 
                          typeof option === 'string' ? option : option.codigo || ''
                        }
                        inputValue={comp.codigoComponente || ""}
                        onInputChange={(_, newValue) => {
                          const valor = newValue.toUpperCase();
                          handleAlterarCampo(comp.sequencia!, "codigoComponente", valor);
                          buscarPerfis(valor);
                        }}
                        onChange={(_, newValue) => {
                          if (newValue && typeof newValue !== 'string') {
                            // FASE 05 — 6ª validação/PO 2026-08-07: preencher código, descrição, id e
                            // tipo num ÚNICO update. Antes eram 3-4 chamadas separadas sobre o mesmo
                            // estado (não-funcional), e só a última valia — a Descrição se perdia.
                            // FASE 05 — persistência 2026-08-10: o Tipo (PosTrat) vem do PerfilComposto
                            // do perfil selecionado — 0=Cor Única,1=Interno,2=Externo (3=Composto cai
                            // em Cor Única na composição).
                            const mapaTipo: Record<number, 'cor_unica' | 'interno' | 'externo'> = { 0: 'cor_unica', 1: 'interno', 2: 'externo', 3: 'cor_unica' };
                            const tipoComp = mapaTipo[newValue.perfilComposto ?? 0] ?? 'cor_unica';
                            onChange(
                              composicao.map((c) =>
                                c.sequencia === comp.sequencia
                                  ? {
                                      ...c,
                                      codigoComponente: newValue.codigo,
                                      descricaoComponente: newValue.descricao,
                                      idPerfilComponente: newValue.idPerfil,
                                      tipoPerfilComponente: tipoComp,
                                    }
                                  : c
                              )
                            );
                          }
                        }}
                        loading={carregandoPerfis}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Código"
                            placeholder="Ex: 000017A"
                            required
                            InputLabelProps={{ sx: labelSx }}
                            sx={inputSx}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.idPerfil}>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {option.codigo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.descricao}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        label="Descrição"
                        value={comp.descricaoComponente || ""}
                        onChange={(e) =>
                          handleAlterarCampo(
                            comp.sequencia!,
                            "descricaoComponente",
                            e.target.value
                          )
                        }
                        placeholder="Ex: PERFIL 000017A - INTERNO"
                        required
                        InputLabelProps={{ sx: labelSx }}
                        sx={inputSx}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <FormControl fullWidth>
                        <InputLabel sx={labelSx}>Tipo do perfil</InputLabel>
                        <Select
                          value={comp.tipoPerfilComponente || "cor_unica"}
                          label="Tipo do perfil"
                          onChange={(e) =>
                            handleAlterarCampo(
                              comp.sequencia!,
                              "tipoPerfilComponente",
                              e.target.value
                            )
                          }
                          sx={selectSx}
                        >
                          <MenuItem value="cor_unica">COR ÚNICA</MenuItem>
                          <MenuItem value="interno">INTERNO</MenuItem>
                          <MenuItem value="externo">EXTERNO</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <IconButton
                        onClick={() => setItemParaRemover(comp.sequencia!)}
                        sx={{ color: "#f44336" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Box>
        )}
      </Grid>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog open={itemParaRemover !== null} onClose={() => setItemParaRemover(null)}>
        <DialogTitle>Remover componente</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja remover este componente?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemParaRemover(null)}>Cancelar</Button>
          <Button
            onClick={() => itemParaRemover !== null && handleRemover(itemParaRemover)}
            color="error"
            variant="contained"
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
